import { motion } from 'framer-motion';
import { Check, Lock, Sparkles } from 'lucide-react';
import { cn } from '../ui/utils';
import { getMissionMeta, TONE_GRADIENTS } from './mission-icons';

export function MissionCard({
  id,
  index = 0,
  title,
  description,
  points,
  status,
  requiresPro,
  monthly,
  monthlyLabel,
  onUpgradeClick,
}) {
  const isCompleted = status === 'completed';
  const isLocked = status === 'locked';
  const meta = getMissionMeta(id);
  const Icon = meta.icon || Sparkles;
  const toneClass = TONE_GRADIENTS[meta.tone] || TONE_GRADIENTS.blue;

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, delay: 0.04 * index, ease: [0.22, 1, 0.36, 1] }}
      whileHover={!isLocked ? { x: 2 } : undefined}
      className={cn(
        'group relative flex items-center gap-3 rounded-xl border p-3.5 transition-shadow sm:gap-4 sm:p-4',
        isCompleted
          ? 'border-emerald-200/60 bg-gradient-to-r from-emerald-50/60 to-white'
          : isLocked
            ? 'border-slate-200/60 bg-slate-50/60 opacity-80'
            : 'border-slate-200/60 bg-white hover:shadow-md'
      )}
    >
      {/* Icon esquerda — único por missao, animado */}
      <motion.div
        whileHover={!isLocked ? { rotate: 8, scale: 1.08 } : undefined}
        animate={isCompleted ? { scale: [1, 1.05, 1] } : undefined}
        transition={isCompleted ? { duration: 2.4, repeat: Infinity, ease: 'easeInOut' } : undefined}
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
          toneClass,
          isLocked && 'grayscale'
        )}
      >
        <Icon className="h-5 w-5" />
      </motion.div>

      {/* Texto */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p
            className={cn(
              'truncate text-sm font-semibold',
              isCompleted ? 'text-slate-700 line-through decoration-emerald-400' : 'text-slate-900'
            )}
          >
            {title}
          </p>
          {monthly && (
            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-700">
              {monthlyLabel || 'Mensal'}
            </span>
          )}
        </div>
        {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
      </div>

      {/* Direita: upgrade + XP + status icon */}
      <div className="flex shrink-0 items-center gap-2">
        {requiresPro && isLocked && (
          <button
            type="button"
            onClick={onUpgradeClick}
            className="rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 px-3 py-1 text-[11px] font-semibold text-white shadow-sm hover:from-blue-600 hover:to-indigo-700"
          >
            Upgrade
          </button>
        )}
        <span
          className={cn(
            'rounded-full px-3 py-1 text-xs font-bold tabular-nums',
            isCompleted
              ? 'bg-emerald-100 text-emerald-700'
              : isLocked
                ? 'bg-slate-100 text-slate-500'
                : 'bg-blue-50 text-blue-700'
          )}
        >
          +{points} XP
        </span>
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            isCompleted
              ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30'
              : isLocked
                ? 'bg-slate-200 text-slate-500'
                : 'bg-slate-100 text-slate-300'
          )}
        >
          {isCompleted ? (
            <Check className="h-4 w-4" />
          ) : isLocked ? (
            <Lock className="h-4 w-4" />
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}

export default MissionCard;
